/**
 * Draws the stage at the screen's real pixel count, not the logical one.
 *
 * The game is laid out in a logical stage (1280x720, or 720 wide on a phone)
 * and Phaser sizes its canvas to match. The browser then stretched that canvas
 * to fill the screen - 1.56x on an iPhone 14, 1.5x on a 1920-wide monitor - so
 * every sprite, line and letter went soft after it was drawn, however sharp
 * the art itself was.
 *
 * Phaser 3 has no render-resolution setting, and zooming every camera would
 * move the HUD and break camera follow and bounds. So this works one level
 * down: the canvas backing store is made `resolution` times bigger, while the
 * renderer's projection stays the logical stage size. Everything is laid out,
 * hit-tested and scrolled in logical units exactly as before, and simply drawn
 * onto more pixels. Camera scissors are the one thing given in pixels, so
 * they are scaled to match.
 *
 * WebGL only. The Canvas fallback keeps the old stretched behaviour.
 */
import Phaser from 'phaser';
import { GAME_HEIGHT, GAME_WIDTH } from '../types';
import { stageResolution } from './stageResolution';

type Renderer = Phaser.Renderer.WebGL.WebGLRenderer;

/** Patch the renderer once it exists; call before the first frame is drawn. */
export function installHiDpiRenderer(game: Phaser.Game): void {
  const renderer = game.renderer;
  if (!(renderer instanceof Phaser.Renderer.WebGL.WebGLRenderer)) return;
  const resolution = stageResolution();
  if (resolution === 1) return;

  const canvas = game.canvas;
  const originalResize = renderer.resize.bind(renderer);
  const originalProjection = renderer.setProjectionMatrix.bind(renderer);
  const originalScissor = renderer.setScissor.bind(renderer);

  // Whatever asks for the full backing-store size (a resize, the reset after
  // a capture) gets the logical stage instead: the same picture, more pixels.
  renderer.setProjectionMatrix = (width: number, height: number): Renderer => {
    const full = width === renderer.width && height === renderer.height;
    return full
      ? originalProjection(width / resolution, height / resolution)
      : originalProjection(width, height);
  };

  // Camera scissors arrive in logical pixels; GL wants backing-store pixels.
  // pushScissor always passes a buffer height, so "the canvas" is told apart
  // from a render target by that height, not by whether one was given.
  renderer.setScissor = (
    x: number,
    y: number,
    width: number,
    height: number,
    drawingBufferHeight?: number,
  ): void => {
    const canvasBuffer =
      drawingBufferHeight === undefined || drawingBufferHeight === renderer.drawingBufferHeight;
    if (!canvasBuffer) {
      originalScissor(x, y, width, height, drawingBufferHeight);
      return;
    }
    originalScissor(
      x * resolution,
      y * resolution,
      width * resolution,
      height * resolution,
      drawingBufferHeight,
    );
  };

  renderer.resize = (width: number, height: number): Renderer => {
    // The scale manager only ever asks for the logical size.
    const deviceWidth = Math.round(width * resolution);
    const deviceHeight = Math.round(height * resolution);
    canvas.width = deviceWidth;
    canvas.height = deviceHeight;
    originalResize(deviceWidth, deviceHeight);
    // Pipelines size their own projection on resize; bring them back to the stage.
    for (const pipeline of renderer.pipelines.pipelines.values()) {
      pipeline.setProjectionMatrix(width, height);
    }
    return renderer;
  };

  renderer.resize(GAME_WIDTH, GAME_HEIGHT);
}
